import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import OutsideClickWrapper from "@/components/hooks/use-outside-click/use-outside-click-wrapper";
import useDropdownSingleOption from "@/components/hooks/use-dropdown-single-option/use-dropdown-single-option";
import OpticksInput from "@/components/ui/OpticksInput/OpticksInput";
import OpticksDropdownButton from "../button/OpticksDropdownButton";
import "./OpticksDropdownSingleOption.css";
import type { OpticksDropdownProps } from "../OpticksDropdown.types";

const OpticksDropdownSingleOption = ({
  direction = "down",
  menuItems,
  disabled,
  placeholder,
  height,
  width = "160px",
  maxWidth,
  behavior,
  surface,
  optionSelected,
  handleChosenOptionChange,
  focusProp,
  icon,
  searchable,
  emptyMessage = "No options available",
}: OpticksDropdownProps) => {
  const {
    open,
    handleOpenLocal,
    handleClickOutside,
    handleChosenOption,
    menuHeight,
    menuRef,
    computedDirection,
    containerRef,
  } = useDropdownSingleOption({
    direction,
    menuItemsLength: menuItems.length,
    focusProp,
    handleChosenOptionChange,
  });

  // Search state (only relevant when searchable)
  const [inputValue, setInputValue] = useState("");
  const searchBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!searchable) return;
    let timeoutId: number | undefined;
    if (!open) {
      timeoutId = window.setTimeout(() => {
        setInputValue("");
      }, 500);
    } else {
      timeoutId = window.setTimeout(() => {
        searchBoxRef.current?.querySelector<HTMLInputElement>("input")?.focus();
      }, 200);
    }
    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [open, searchable]);

  const filteredItems = searchable
    ? (() => {
        const query = inputValue.trim().toLowerCase();
        if (!query) return menuItems;
        return menuItems.filter((d) => {
          const haystack = d.searchText ?? d.label;
          return (
            typeof haystack === "string" &&
            haystack.toLowerCase().includes(query)
          );
        });
      })()
    : menuItems;

  return (
    <OutsideClickWrapper callback={handleClickOutside} width={width}>
      <div className="opticks-dropdown-single-option" ref={containerRef}>
        <OpticksDropdownButton
          handleOpen={handleOpenLocal}
          disabled={disabled}
          direction={computedDirection}
          behavior={behavior}
          surface={surface}
          width={width}
          maxWidth={maxWidth}
          placeholder={placeholder}
          optionSelected={optionSelected}
          open={open}
          icon={icon}
        />

        <div
          className={classNames(
            "opticks-dropdown-menu",
            { open: open, "height-constrained": open && !!height },
            { [`surface-${surface}`]: surface },
            { [`behavior-${behavior}`]: behavior },
            { [`direction-${computedDirection}`]: computedDirection }
          )}
          ref={menuRef}
          style={{
            minWidth: width,
            maxWidth: maxWidth || width,
            ...(open && {
              maxHeight: height || menuHeight,
            }),
          }}
        >
          {searchable && (
            <div className="opticks-dropdown-search-box" ref={searchBoxRef}>
              <OpticksInput
                withSearchIcon
                placeholder="Find..."
                input={{
                  className: "avoid-box-shadow",
                  value: inputValue,
                  onChange: (ev: React.ChangeEvent<HTMLInputElement>) =>
                    setInputValue(ev.target.value),
                }}
              />
            </div>
          )}

          {searchable && filteredItems.length === 0 ? (
            <div className="opticks-dropdown-no-items">{emptyMessage}</div>
          ) : (
            <>
              {filteredItems.map((menuItem, index) =>
                menuItem.separator ? (
                  <hr
                    key={`sep-${index}`}
                    className="opticks-dropdown-menu-separator"
                  />
                ) : menuItem.limits ? (
                  <div
                    key={`lim-${index}`}
                    className="opticks-dropdown-menu-limit"
                  />
                ) : (
                  <div
                    onClick={(e) =>
                      !menuItem.disabled && handleChosenOption(e, menuItem)
                    }
                    key={menuItem.value as React.Key}
                    className={classNames("opticks-dropdown-menu-item", {
                      "selected-item": menuItem.value === optionSelected?.value,
                      disabled: menuItem.disabled,
                    })}
                  >
                    <span>
                      {typeof menuItem.label === "string" ? (
                        <span>{menuItem.label}</span>
                      ) : (
                        menuItem.label
                      )}
                    </span>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    </OutsideClickWrapper>
  );
};

export default React.memo(OpticksDropdownSingleOption);
